import Phaser from 'phaser';
import { GAME_WIDTH } from '../data/gameConstants';
import { ResponsiveSystem } from '../systems/ResponsiveSystem';

export interface PanelBounds {
  panelX: number;
  panelY: number;
  panelTop: number;
  panelBottom: number;
  panelW: number;
  panelH: number;
  contentTop: number;
  contentMaxH: number;
  backButtonY: number;
}

export interface PanelBoundsOptions {
  /** Panel teks scroll (Cara Bermain, Panduan, Credit) */
  scroll?: boolean;
}

const SIDE_MARGIN = 24;
const SAFE_TOP = 40;
const SAFE_BOTTOM = 44;

/** Zona header modal: judul → garis pemisah → isi */
const MODAL_HEADER_TOP = 36;
const MODAL_TITLE_Y = 38;
const MODAL_DIVIDER_Y = 66;
const MODAL_BODY_TOP = 76;

const TITLE_ZONE = 76;
const FOOTER_ZONE = 88;

const SCROLL_TITLE_ZONE = 104;
const SCROLL_TITLE_GAP = 24;
const SCROLL_FOOTER_ZONE = 124;
const SCROLL_BACK_INSET = 72;
const SCROLL_CONTENT_PAD = 32;

const CONTENT_PAD_X = 40;

/** Teks referensi — ukuran kotak Credit jadi standar semua panel scroll */
export const SCROLL_PANEL_REFERENCE_LINES = [
  'SEISM — Disaster Response Simulation',
  'Game edukasi simulasi tanggap bencana',
  '',
  'Latar: Kerajaan Peremban Panas, Bumi Raflesia',
  'Provinsi Bengkulu, Indonesia',
  '',
  'Konten mitigasi bencana',
  'berkolaborasi prinsip BMKG & BNPB',
  '',
  'Versi 1.0.0 — SEISM Final Release',
];

/** Tinggi panel Mulai Game — pas dengan isi, tanpa area kosong besar */
export function getStartGamePanelHeight(
  buttonHeight: number,
  options: { hasDetailedSave: boolean; hasSave: boolean; storageReady: boolean },
): number {
  const { hasDetailedSave, hasSave, storageReady } = options;
  let h = MODAL_BODY_TOP + FOOTER_ZONE;
  if (hasDetailedSave) {
    h += 80;
  } else if (hasSave || !storageReady) {
    h += storageReady ? 44 : 60;
  } else {
    h += 44;
  }
  h += buttonHeight * 2 + 14 + 16;
  return h;
}

/** Tinggi panel modal umum */
export function getModalPanelHeight(
  buttonHeight: number,
  options?: {
    textRows?: number;
    actionButtons?: number;
    hasSubtitle?: boolean;
    bodyBlockHeight?: number;
  },
): number {
  const textRows = options?.textRows ?? 0;
  const actionButtons = options?.actionButtons ?? 1;
  const hasSubtitle = options?.hasSubtitle ?? false;
  const bodyBlockHeight = options?.bodyBlockHeight ?? 0;

  let h = MODAL_BODY_TOP + FOOTER_ZONE;
  if (bodyBlockHeight > 0) {
    h += bodyBlockHeight + 12;
  } else {
    if (hasSubtitle) h += 28 + 16;
    if (textRows > 0) h += textRows * 26 + 12;
  }
  h += actionButtons * buttonHeight + Math.max(0, actionButtons - 1) * 18;
  h += 16;
  return h;
}

export function getModalDividerY(panelTop: number): number {
  return panelTop + MODAL_DIVIDER_Y;
}

export function getModalBodyTop(panelTop: number): number {
  return panelTop + MODAL_BODY_TOP;
}

/** Titik tengah vertikal kotak info save */
export function getModalSaveCardCenterY(panelTop: number, saveCardHeight: number): number {
  return panelTop + MODAL_BODY_TOP + saveCardHeight / 2;
}

/** Baris teks info progress — di dalam kotak isi, terpusat vertikal */
export function getModalSubtitleY(panelTop: number, saveCardHeight = 48, lineIndex = 0, lineCount = 1): number {
  const centerY = getModalSaveCardCenterY(panelTop, saveCardHeight);
  if (lineCount <= 1) return centerY;
  const lineGap = 22;
  const blockOffset = ((lineCount - 1) * lineGap) / 2;
  return centerY - blockOffset + lineIndex * lineGap;
}

/** Posisi Y tengah tombol aksi pertama */
export function getModalFirstButtonY(
  panelTop: number,
  buttonHeight: number,
  bodyBlockHeight: number,
): number {
  return panelTop + MODAL_BODY_TOP + bodyBlockHeight + 20 + buttonHeight / 2;
}

export function getPanelBounds(
  scene: Phaser.Scene,
  panelW: number,
  panelH: number,
  options?: PanelBoundsOptions,
): PanelBounds {
  const scroll = options?.scroll ?? false;
  const titleZone = scroll ? SCROLL_TITLE_ZONE : TITLE_ZONE;
  const footerZone = scroll ? SCROLL_FOOTER_ZONE : FOOTER_ZONE;
  const titleGap = scroll ? SCROLL_TITLE_GAP : 0;
  const backInset = scroll ? SCROLL_BACK_INSET : 52;

  const visible = ResponsiveSystem.getVisibleGameRect(scene.game);
  const safeTop = visible.top + SAFE_TOP;
  const safeBottom = visible.bottom - SAFE_BOTTOM;
  const safeH = Math.max(240, safeBottom - safeTop);

  const clampedH = Math.min(panelH, safeH);
  const clampedW = Math.min(panelW, GAME_WIDTH - SIDE_MARGIN * 2);

  const panelX = GAME_WIDTH / 2;
  const panelY = safeTop + safeH / 2;
  const panelTop = panelY - clampedH / 2;
  const panelBottom = panelY + clampedH / 2;

  const contentTop = panelTop + titleZone + titleGap;
  const contentMaxH = clampedH - titleZone - titleGap - footerZone;
  const backButtonY = panelBottom - backInset;

  return {
    panelX,
    panelY,
    panelTop,
    panelBottom,
    panelW: clampedW,
    panelH: clampedH,
    contentTop,
    contentMaxH,
    backButtonY,
  };
}

export function getScrollPanelWidth(isCompact: boolean): number {
  return Math.min(GAME_WIDTH - SIDE_MARGIN * 2, isCompact ? GAME_WIDTH - 40 : 820);
}

export function getScrollChromeHeight(): number {
  return SCROLL_TITLE_ZONE + SCROLL_TITLE_GAP + SCROLL_CONTENT_PAD + SCROLL_FOOTER_ZONE;
}

export function getUnifiedScrollPanelHeight(contentHeight: number): number {
  return getScrollChromeHeight() + contentHeight;
}

/** @deprecated use getUnifiedScrollPanelHeight */
export function getScrollPanelHeight(
  _scene: Phaser.Scene,
  contentHeight: number,
  _isCompact: boolean,
): number {
  return getUnifiedScrollPanelHeight(contentHeight);
}

export function getScrollContentWidth(panelW: number): number {
  return panelW - CONTENT_PAD_X * 2;
}

export function getTitleY(panelTop: number, scroll = false): number {
  if (scroll) return panelTop + 58;
  return panelTop + MODAL_TITLE_Y;
}

/** @deprecated use getScrollPanelWidth + getScrollPanelHeight */
export function getScrollPanelSize(isCompact: boolean): { panelW: number; panelH: number } {
  return {
    panelW: getScrollPanelWidth(isCompact),
    panelH: isCompact ? 560 : 600,
  };
}

export { MODAL_HEADER_TOP };
